import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, ChatCircleText, ImageBroken, ClockCounterClockwise, FileText, Flag, Megaphone, Scales, IdentificationCard, List, MapPin,
  HardDrives, MagnifyingGlass, RocketLaunch, SignOut, Tray, SlidersHorizontal, SquaresFour, Sparkle, Tag, ToggleRight, UserGear, UsersThree, Waveform,
} from "@phosphor-icons/react";
import { useAdmin, useConsoleLanguage } from "@/admin/AuthContext";
import { LogoMark } from "@/lib/brand";
import { api } from "@/lib/api";
import { currentLang, tr } from "@/i18n/translate";
import LanguagePicker from "@/components/LanguagePicker";
import { Pill } from "@/components/admin/AdminUI";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";

const O = ["owner"];
const OM = ["owner", "moderator"];
const OS = ["owner", "support"];
const ALL = ["owner", "moderator", "support"];

// Mirrors the backend role map (console_api_prompt9.md). The server still enforces every call.
export const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: SquaresFour, roles: O },
  { to: "/admin/users", label: "Users", icon: UsersThree, roles: ALL },
  { to: "/admin/discover-check", label: "Discover check", icon: MagnifyingGlass, roles: O },
  { to: "/admin/reports", label: "Reports", icon: Flag, roles: OM },
  { to: "/admin/appeals", label: "Appeals", icon: Scales, roles: OM },
  { to: "/admin/reviews", label: "Verification", icon: IdentificationCard, roles: OM },
  { to: "/admin/photos", label: "Rejected media", icon: ImageBroken, roles: OM },
  { to: "/admin/glass", label: "Glass intros", icon: Waveform, roles: OM },
  { to: "/admin/prompts", label: "Prompts", icon: ChatCircleText, roles: O },
  { to: "/admin/interests", label: "Interests", icon: Sparkle, roles: O },
  { to: "/admin/launch", label: "Launch", icon: RocketLaunch, roles: OS },
  { to: "/admin/inbox", label: "Support inbox", icon: Tray, roles: OS },
  { to: "/admin/eligibility", label: "Eligibility", icon: MapPin, roles: O },
  { to: "/admin/pricing", label: "Pricing", icon: Tag, roles: O },
  { to: "/admin/promotions", label: "Promotions", icon: Megaphone, roles: O },
  { to: "/admin/content", label: "Content", icon: FileText, roles: O },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, roles: O },
  { to: "/admin/flags", label: "Feature flags", icon: ToggleRight, roles: O },
  { to: "/admin/settings", label: "Settings", icon: SlidersHorizontal, roles: O },
  { to: "/admin/storage", label: "Storage", icon: HardDrives, roles: O },
  { to: "/admin/audit", label: "Admin Log", icon: ClockCounterClockwise, roles: O },
  { to: "/admin/team", label: "Team", icon: UserGear, roles: O },
];

export const navFor = (role) => NAV.filter((n) => n.roles.includes(role));

// /admin lands on the first page this role can open.
export function AdminHome() {
  const { admin } = useAdmin();
  return <Navigate to={navFor(admin?.role)[0]?.to || "/admin/users"} replace />;
}

const linkCls = ({ isActive }) =>
  `flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors cw-ease ${isActive ? "cw-bg-tint cw-brand-deep" : "cw-soft hover:bg-[var(--cw-surface-tertiary)] hover:text-foreground"}`;

function AccountLanguage() {
  const { admin, setLanguage } = useAdmin();
  const [languages, setLanguages] = useState([]);
  useEffect(() => { api("/i18n/languages", { auth: false }).then((d) => setLanguages(d?.items || [])).catch(() => {}); }, []);
  const change = (code) => setLanguage(code).then(() => toast.success(tr("Language saved."))).catch((e) => toast.error(e.message));
  return <LanguagePicker variant="console" value={admin?.language || currentLang()} languages={languages} onChange={change} side="top" align="start" testId="admin-language" />;
}

function Sidebar({ onNavigate }) {
  const { admin, signOut } = useAdmin();
  return (
    <div className="flex h-full flex-col" data-testid="admin-sidebar">
      <div className="flex items-center gap-3 px-3 pb-6 pt-2">
        <LogoMark className="h-9 w-9" testId="admin-sidebar-logo" />
        <div><p className="font-semibold leading-tight">Crushwink</p><p className="cw-muted text-xs">Console</p></div>
      </div>
      <nav aria-label="Admin" className="-mx-1 flex-1 space-y-0.5 overflow-y-auto px-1">
        {navFor(admin?.role).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={linkCls} onClick={onNavigate} data-testid={`admin-nav-${to.split("/").pop()}`}>
            <Icon size={19} aria-hidden />{label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-4 rounded-2xl bg-[var(--cw-surface-tertiary)] p-4" data-testid="admin-sidebar-account">
        <p className="truncate text-sm font-medium" data-testid="admin-current-email" data-no-i18n>{admin?.email}</p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <Pill tone={admin?.role === "owner" ? "brand" : "neutral"} className="capitalize" data-testid="admin-current-role">{admin?.role}</Pill>
          <AccountLanguage />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <ChangePasswordDialog />
          <button onClick={signOut} className="cw-soft inline-flex h-9 items-center gap-2 rounded-lg px-2 text-sm hover:text-foreground" data-testid="admin-logout-button">
            <SignOut size={18} aria-hidden />Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShell() {
  const [open, setOpen] = useState(false);
  useConsoleLanguage();
  return (
    <div className="min-h-screen bg-background" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-border bg-white p-4 lg:block">
        <Sidebar />
      </aside>
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-white px-4 lg:hidden">
        <div className="flex items-center gap-2"><LogoMark className="h-8 w-8" testId="admin-topbar-logo" /><span className="font-semibold">Console</span></div>
        <button onClick={() => setOpen(true)} aria-label="Open menu" className="flex h-11 w-11 items-center justify-center rounded-xl" data-testid="admin-mobile-menu-button"><List size={22} /></button>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 bg-white p-4" data-testid="admin-mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Sidebar onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8 lg:py-12"><Outlet /></div>
      </main>
    </div>
  );
}
